/**
 * Écrans activités : liste avec inscription directe depuis la carte (§ 9.2),
 * détail, création (moniteur/comité), gestion des inscrits (responsable/comité).
 */
import React, { useState } from 'react';
import { api, uuid, isStandalone } from '../lib/api.js';
import {
  Spinner, ErrorBox, OfflineNote, Badge, useApi, UserSearch, Intro,
  IconeCorbeille, IconeCrayon, IconeCalendrier, IconeLieu, IconePersonne, IconeType,
  peut, groupesDe,
} from '../components/common.jsx';
import { fmtDateHeure, fmtDateHeureMaj } from '../lib/format.js';
import { lienGoogle, telechargerIcs } from '../lib/calendrier.js';

const TYPES = { animation: 'Animation', initiation: 'Cours débutants', competition: 'Compétition', assemblee: 'Assemblée', autre: 'Autre' };

/**
 * Types écartés de la page Activités : animations et cours débutants ont
 * leurs propres écrans. Filtré au rendu — le cache `activites_all` est
 * partagé avec « Mes inscriptions », qui doit continuer à tout voir.
 */
const TYPES_MASQUES = ['animation', 'initiation'];

/** États pour lesquels le membre est considéré comme engagé. */
const ETATS_ACTIFS = ['inscrit', 'liste_attente', 'excuse'];

export function Activites({ onOpen, me, toast }) {
  const [state, reload] = useApi('activites', { cache: 'activites_all' });
  const [filtre, setFiltre] = useState('');
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const visibles = state.data.filter((a) => !TYPES_MASQUES.includes(a.type) && (!filtre || a.type === filtre));
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="h4 mb-0">Activités du club</h2>
        {peut(me, 'adlt_manage_activites') && (
          <button className="btn btn-primary btn-sm" onClick={() => onOpen('creer')}>+ Créer une activité</button>
        )}
      </div>
      <Intro>Les activités du club ouvertes à l'inscription : compétitions, assemblées
        et autres rendez-vous. Inscrivez-vous directement depuis chaque carte.</Intro>
      <OfflineNote result={state} />
      <FiltreType filtre={filtre} setFiltre={setFiltre} />
      {visibles.length === 0 && <p className="text-muted">Aucune activité à venir.</p>}
      <div className="row g-3">
        {visibles.map((a) => (
          <div className="col-md-6" key={a.id}>
            <CarteActivite a={a} onOpen={onOpen} toast={toast} onChange={reload} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Carte d'activité avec inscription directe : l'inscription ne doit plus
 * imposer d'ouvrir le détail puis de chercher le bouton.
 */
function CarteActivite({ a, onOpen, toast, onChange }) {
  const [busy, setBusy] = useState(false);
  const mienne = a.mon_inscription;
  const inscrit = mienne && ETATS_ACTIFS.includes(mienne.etat);

  async function inscrire(e) {
    // La carte entière ouvre le détail : le bouton ne doit pas déclencher les deux.
    e.stopPropagation();
    setBusy(true);
    const r = await api(`activites/${a.id}/inscription`, {
      method: 'POST',
      body: { etat: 'inscrit', remarque: '', nb_invites: 0 },
      queueable: true,
      idempotencyKey: uuid(),
      label: `Inscription — ${a.titre}`,
    });
    setBusy(false);
    if (r.ok && r.queued) toast?.('Hors ligne : votre inscription sera transmise à la reconnexion.');
    else if (r.ok) {
      toast?.(r.data.etat === 'liste_attente' ? "Activité complète : vous êtes en liste d'attente." : "C'est noté, vous êtes inscrit !");
      onChange?.();
    } else toast?.(r.error, true);
  }

  const complete = a.capacite_max !== null && a.nb_inscrits >= a.capacite_max && !a.liste_attente_active;
  return (
    <div className="card h-100 adlt-carte-activite" role="button" tabIndex={0}
      onClick={() => onOpen(a.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(a.id); } }}>
      {a.image && <img src={a.image} className="card-img-top" alt="" loading="lazy" style={{ maxHeight: 150, objectFit: 'cover' }} />}
      <div className="card-body d-flex flex-column">
        {/* Le statut « Publiée » n'apprend rien — toutes les activités listées
            le sont — et mangeait une ligne. Seuls les statuts inhabituels
            (annulée, brouillon) restent affichés. */}
        <div className="d-flex justify-content-between align-items-start gap-2">
          <h5 className="card-title mb-1">{a.titre}</h5>
          {a.statut !== 'publiee' && <Badge statut={a.statut} />}
        </div>
        <span className="badge bg-light text-dark border mb-2 align-self-start d-inline-flex align-items-center gap-1">
          <IconeType type={a.type} /> {TYPES[a.type] || a.type}
        </span>
        <p className="text-muted small mb-1 d-flex align-items-center gap-2">
          <IconeCalendrier taille={14} /> <span>{fmtDateHeureMaj(a.debut)}</span>
        </p>
        {a.lieu && (
          <p className="text-muted small mb-1 d-flex align-items-center gap-2">
            <IconeLieu /> <span>{a.lieu}</span>
          </p>
        )}
        <p className="text-muted small mb-2 d-flex align-items-center gap-2">
          <IconePersonne />
          <span>{a.responsable}{a.capacite_max !== null && <> · {a.nb_inscrits}/{a.capacite_max} places</>}</span>
        </p>
        <div className="mt-auto pt-1 d-grid gap-2">
          {a.statut !== 'publiee' ? null : inscrit ? (
            <span className={`badge ${mienne.etat === 'inscrit' ? 'bg-success' : mienne.etat === 'excuse' ? 'bg-warning text-dark' : 'bg-info text-dark'} py-2 px-3`}>
              {mienne.etat === 'inscrit' ? '✓ Je suis inscrit' : mienne.etat === 'excuse' ? 'Je me suis excusé' : "Je suis en liste d'attente"}
            </span>
          ) : !a.inscription_ouverte ? (
            <span className="text-muted small">Délai d'inscription échu.</span>
          ) : (
            <button className="btn btn-primary" disabled={busy} onClick={inscrire}>
              {busy ? '…' : complete ? "S'inscrire (complet)" : "S'inscrire"}
            </button>
          )}
          <BoutonsCalendrier activite={a} />
        </div>
      </div>
    </div>
  );
}

/**
 * Ajout à l'agenda personnel. Deux chemins parce qu'aucun ne couvre tout le
 * monde : le .ics (Apple, Outlook, Android) et le lien Google Agenda.
 * `stopPropagation` partout : la carte entière ouvre le détail.
 */
export function BoutonsCalendrier({ activite, petit = true }) {
  const [ouvert, setOuvert] = useState(false);
  const evenement = {
    id: activite.id,
    titre: activite.titre,
    debut: activite.debut,
    fin: activite.fin,
    lieu: activite.lieu,
    description: (activite.description || '').replace(/<[^>]+>/g, '').slice(0, 500),
  };
  const taille = petit ? ' btn-sm' : '';
  if (!ouvert) {
    return (
      <button type="button" className={`btn btn-outline-secondary${taille} d-flex align-items-center justify-content-center gap-2`}
        onClick={(e) => { e.stopPropagation(); setOuvert(true); }}>
        <IconeCalendrier taille={14} /> Ajouter au calendrier
      </button>
    );
  }
  return (
    <div className="d-grid gap-1" onClick={(e) => e.stopPropagation()}>
      <a className={`btn btn-outline-secondary${taille}`} href={lienGoogle(evenement)}
        target="_blank" rel="noopener noreferrer">Google Agenda</a>
      <button type="button" className={`btn btn-outline-secondary${taille}`}
        onClick={() => telechargerIcs(evenement)}>Apple, Outlook (.ics)</button>
    </div>
  );
}

/** Liste + détail autonomes (backoffice et shortcodes, sans routeur). */
export function ActivitesAvecDetail({ me, toast }) {
  const [ouverte, setOuverte] = useState(null); // id | 'creer' | null
  if (ouverte === 'creer') {
    return <ActiviteCreer me={me} toast={toast} groupes={groupesDe(me)} onDone={(id) => setOuverte(id)} />;
  }
  if (ouverte) {
    return <ActiviteDetail id={ouverte} me={me} toast={toast} onBack={() => setOuverte(null)} />;
  }
  return <Activites me={me} toast={toast} onOpen={setOuverte} />;
}

/** Filtres par type — animations et cours débutants ont leurs propres pages. */
function FiltreType({ filtre, setFiltre }) {
  const options = [['', 'Toutes'], ['competition', 'Compétitions'], ['assemblee', 'Assemblées'], ['autre', 'Autres']];
  return (
    <div className="d-flex gap-2 flex-wrap mb-3">
      {options.map(([v, l]) => (
        <button type="button" key={v}
          className={`btn btn-sm ${filtre === v ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setFiltre(v)}>{l}</button>
      ))}
    </div>
  );
}

export function ActiviteDetail({ id, me, onBack, toast }) {
  const [state, reload] = useApi('activites/' + id, { cache: 'activite_' + id, deps: [id] });
  const [remarque, setRemarque] = useState('');
  const [invites, setInvites] = useState(0);
  const [modification, setModification] = useState(false);
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const a = state.data;
  const mienne = a.mon_inscription;

  async function agir(etat) {
    const r = await api(`activites/${id}/inscription`, {
      method: 'POST',
      body: { etat, remarque, nb_invites: invites },
      queueable: true,
      idempotencyKey: uuid(),
      label: `Inscription — ${a.titre}`,
    });
    if (r.ok && r.queued) toast('Hors ligne : votre inscription sera transmise à la reconnexion.');
    else if (r.ok) { toast(r.data.etat === 'liste_attente' ? 'Activité complète : vous êtes en liste d\'attente.' : 'C\'est noté !'); reload(); }
    else toast(r.error, true);
  }

  async function desinscrire() {
    if (!confirm('Confirmer votre désinscription ?')) return;
    const r = await api(`activites/${id}/inscription`, {
      method: 'DELETE', queueable: true, idempotencyKey: uuid(),
      label: `Désinscription — ${a.titre}`,
    });
    if (r.ok && r.queued) toast('Hors ligne : votre désinscription sera transmise à la reconnexion.');
    else if (r.ok) { toast('Désinscription enregistrée.'); reload(); }
    else toast(r.error, true);
  }

  const peutGerer = peut(me, 'adlt_manage_inscriptions') || a.responsable_id === me?.id;

  async function annulerActivite() {
    if (!confirm('Annuler cette activité ? Les inscrits seront notifiés.')) return;
    const r = await api(`activites/${id}/annuler`, { method: 'POST' });
    if (r.ok) { toast('Activité annulée, inscrits notifiés.'); reload(); } else toast(r.error, true);
  }

  async function supprimerActivite() {
    if (!confirm('Supprimer définitivement cette activité et toutes ses inscriptions ?')) return;
    const r = await api(`activites/${id}/supprimer`, { method: 'POST' });
    if (r.ok) { toast('Activité supprimée.'); onBack(); } else toast(r.error, true);
  }

  return (
    <div>
      <button className="btn btn-link px-0" onClick={onBack}>‹ Retour</button>
      {a.image && <img src={a.image} alt="" className="rounded mb-3 w-100" style={{ maxHeight: 260, objectFit: 'cover' }} />}
      <h2 className="h4">{a.titre} <Badge statut={a.statut} /></h2>
      {modification && (
        <div className="card adlt-etape mb-3"><div className="card-body">
          <ActiviteCreer me={me} toast={toast} groupes={groupesDe(me)} initial={a}
            onDone={() => { setModification(false); reload(); }} />
        </div></div>
      )}
      <p className="text-muted">
        {fmtDateHeure(a.debut)} → {fmtDateHeure(a.fin)}<br />
        {a.lieu && <>Lieu : {a.lieu}<br /></>}
        Responsable : {a.responsable}
        {a.clubhouse_requis && <><br />Club-house réservé pour cette activité.</>}
        {a.capacite_max !== null && <><br />Places : {a.nb_inscrits}/{a.capacite_max}{a.liste_attente_active ? ' (liste d\'attente active)' : ''}</>}
        {a.delai_inscription && <><br />Délai d'inscription : {fmtDateHeure(a.delai_inscription)}</>}
      </p>
      {a.description && <div className="card mb-3"><div className="card-body" dangerouslySetInnerHTML={{ __html: a.description }} /></div>}

      {a.statut !== 'annulee' && (
        <div className="mb-3" style={{ maxWidth: 320 }}>
          <BoutonsCalendrier activite={a} petit={false} />
        </div>
      )}

      {/* Actions de gestion : sous la description, avant « Mon inscription ». */}
      {peutGerer && a.statut !== 'annulee' && (
        <div className="d-flex gap-2 flex-wrap mb-3">
          <button className="btn btn-info btn-sm" title="Modifier" aria-label="Modifier l'activité"
            aria-pressed={modification} onClick={() => setModification(!modification)}>
            <IconeCrayon />
          </button>
          <button className="btn btn-secondary btn-sm" onClick={annulerActivite}>Annuler l'activité</button>
          {peut(me, 'adlt_manage_inscriptions') && (
            <button className="btn btn-danger btn-sm" title="Supprimer" aria-label="Supprimer" onClick={supprimerActivite}><IconeCorbeille /></button>
          )}
        </div>
      )}

      {a.statut === 'publiee' && (
        <div className="card mb-3">
          <div className="card-body">
            <h5>Mon inscription {mienne && <Badge statut={mienne.etat} />}</h5>
            {!a.inscription_ouverte ? (
              <p className="text-muted mb-0">Le délai d'inscription est échu.</p>
            ) : (
              <>
                <div className="mb-2">
                  <label className="form-label">Remarque (facultatif)</label>
                  <input className="form-control" value={remarque} onChange={(e) => setRemarque(e.target.value)}
                    placeholder={mienne?.remarque || ''} />
                </div>
                {a.invites_autorises && (
                  <div className="mb-2">
                    <label className="form-label">Invités{a.invites_max !== null ? ` (max. ${a.invites_max})` : ''}</label>
                    <input type="number" min="0" max={a.invites_max ?? 99} className="form-control" style={{ maxWidth: 120 }}
                      value={invites} onChange={(e) => setInvites(Math.max(0, parseInt(e.target.value || '0', 10)))} />
                  </div>
                )}
                <div className="d-flex gap-2 flex-wrap">
                  {(!mienne || mienne.etat !== 'inscrit') && (
                    <button className="btn btn-primary" onClick={() => agir('inscrit')}>Je m'inscris</button>
                  )}
                  {a.excuse_active && (!mienne || mienne.etat !== 'excuse') && (
                    <button className="btn btn-outline-warning" onClick={() => agir('excuse')}>Je m'excuse</button>
                  )}
                  {mienne && ETATS_ACTIFS.includes(mienne.etat) && (
                    <button className="btn btn-outline-danger" onClick={desinscrire}>Me désinscrire</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {peut(me, 'adlt_view_inscrits') && (
        <ActiviteInscrits id={id} me={me} toast={toast} excuseActive={a.excuse_active} />
      )}
    </div>
  );
}

/** Liste des inscrits (responsable / comité, journalisée côté serveur). */
export function ActiviteInscrits({ id, me, toast, excuseActive }) {
  const [state, reload] = useApi('activites/' + id + '/inscriptions', { deps: [id] });
  const peutGerer = peut(me, 'adlt_manage_inscriptions');
  // Coordonnées et remarques ne sont montrées qu'au backoffice : l'application
  // est consultée en salle, écran à la vue de tous.
  const avecCoordonnees = !isStandalone();
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const rows = state.data;
  const actifs = rows.filter((r) => r.etat !== 'annule');
  const sansReponse = excuseActive;

  async function annuler(iid) {
    if (!confirm('Annuler cette inscription ?')) return;
    const r = await api('inscriptions/' + iid, { method: 'PATCH', body: { action: 'annuler' } });
    if (r.ok) { toast('Inscription annulée.'); reload(); } else toast(r.error, true);
  }

  async function ajouter(u) {
    const r = await api(`activites/${id}/inscriptions`, { method: 'POST', body: { user_id: u.id } });
    if (r.ok) { toast(`${u.nom} inscrit(e).`); reload(); } else toast(r.error, true);
  }

  return (
    <div className="card">
      <div className="card-body">
        <h5>Inscrits ({actifs.filter((r) => r.etat === 'inscrit').length})</h5>
        {sansReponse && <p className="text-muted mb-2">Activité avec excuse : les membres sans réponse sont mis en évidence côté backoffice.</p>}
        {peutGerer && (
          <div className="mb-3" style={{ maxWidth: 380 }}>
            <UserSearch onSelect={ajouter} placeholder="Ajouter un membre…" />
          </div>
        )}
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead><tr>
              <th>Nom</th>
              {avecCoordonnees && <th>Contact</th>}
              <th>Invités</th>
              {avecCoordonnees && <th>Remarque</th>}
              <th>État</th>
              {peutGerer && <th></th>}
            </tr></thead>
            <tbody>
              {actifs.map((r) => (
                <tr key={r.id}>
                  <td>{r.nom}{r.source === 'comite' && <small className="text-muted"> (ajout comité)</small>}</td>
                  {avecCoordonnees && <td><small>{r.email}{r.telephone ? ' · ' + r.telephone : ''}</small></td>}
                  <td>{r.nb_invites || ''}</td>
                  {avecCoordonnees && <td><small>{r.remarque}</small></td>}
                  <td><Badge statut={r.etat} /></td>
                  {peutGerer && <td>{ETATS_ACTIFS.includes(r.etat) && (
                    <button className="btn btn-outline-danger btn-sm" onClick={() => annuler(r.id)}>Annuler</button>
                  )}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Choix binaire en bouton — cible tactile, cohérent avec les autres formulaires. */
function BoutonOption({ actif, onToggle, children, aide }) {
  return (
    <div>
      <button type="button" aria-pressed={actif} onClick={() => onToggle(!actif)}
        className={`btn ${actif ? 'btn-primary' : 'btn-outline-primary'} w-100`}>
        {actif ? '✓ ' : ''}{children}
      </button>
      {aide && <small className="text-muted d-block mt-1">{aide}</small>}
    </div>
  );
}

/** Création / modification d'une activité (moniteur, comité). */
export function ActiviteCreer({ me, onDone, toast, groupes, initial }) {
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(null);
  const [listeAttente, setListeAttente] = useState(!!initial?.liste_attente_active);
  const [invitesOk, setInvitesOk] = useState(!!initial?.invites_autorises);
  const [excuse, setExcuse] = useState(!!initial?.excuse_active);
  const [clubhouse, setClubhouse] = useState(!!initial?.clubhouse_requis);
  const edition = !!(initial && initial.id);
  const dtLocal = (iso) => (iso ? String(iso).slice(0, 16) : '');
  function lireImage(e) {
    const fichier = e.target.files && e.target.files[0];
    if (!fichier) { setImage(null); return; }
    if (fichier.size > 3 * 1024 * 1024) { toast('Image trop lourde (3 Mo au maximum).', true); e.target.value = ''; return; }
    const lecteur = new FileReader();
    lecteur.onload = () => setImage(lecteur.result);
    lecteur.readAsDataURL(fichier);
  }
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const f = e.target;
    const r = await api(edition ? 'activites/' + initial.id : 'activites', {
      method: edition ? 'PATCH' : 'POST',
      body: {
        image,
        type: f.type.value, titre: f.titre.value, description: f.description.value,
        debut: f.debut.value.replace('T', ' ') + ':00', fin: f.fin.value.replace('T', ' ') + ':00',
        lieu_libre: f.lieu_libre.value,
        capacite_max: f.capacite_max.value, liste_attente_active: listeAttente ? 1 : 0,
        invites_autorises: invitesOk ? 1 : 0, invites_max: f.invites_max.value,
        delai_inscription: f.delai.value ? f.delai.value.replace('T', ' ') + ':00' : '',
        excuse_active: excuse ? 1 : 0,
        clubhouse_requis: clubhouse ? 1 : 0,
        visibilite: f.visibilite.value, groupe_id: f.groupe_id.value,
        statut: 'publiee',
      },
    });
    setBusy(false);
    if (r.ok) { toast(edition ? 'Activité modifiée.' : 'Activité créée et publiée.'); onDone(edition ? initial.id : r.data.id); } else toast(r.error, true);
  }
  return (
    <form onSubmit={submit}>
      <h2 className="h4 mb-3">{edition ? 'Modifier l\'activité' : 'Créer une activité'}</h2>
      <div className="row g-3">
        <div className="col-md-8"><label className="form-label">Titre *</label><input name="titre" className="form-control" required defaultValue={initial?.titre || ''} /></div>
        <div className="col-md-4"><label className="form-label">Type</label>
          <select name="type" className="form-select" defaultValue={initial?.type || 'autre'}>{Object.entries(TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div className="col-md-6"><label className="form-label">Début *</label><input name="debut" type="datetime-local" className="form-control" required defaultValue={dtLocal(initial?.debut)} /></div>
        <div className="col-md-6"><label className="form-label">Fin *</label><input name="fin" type="datetime-local" className="form-control" required defaultValue={dtLocal(initial?.fin)} /></div>
        <div className="col-md-6"><label className="form-label">Lieu</label><input name="lieu_libre" className="form-control" defaultValue={initial?.lieu || ''} /></div>
        <div className="col-md-3"><label className="form-label">Capacité max.</label><input name="capacite_max" type="number" min="0" className="form-control" defaultValue={initial?.capacite_max ?? ''} /></div>
        <div className="col-md-3"><label className="form-label">Délai d'inscription</label><input name="delai" type="datetime-local" className="form-control" defaultValue={dtLocal(initial?.delai_inscription)} /></div>
        <div className="col-md-4"><label className="form-label">Visibilité</label>
          <select name="visibilite" className="form-select" defaultValue={initial?.visibilite || 'membres'}><option value="membres">Membres</option><option value="publique">Publique</option><option value="groupe">Groupe</option></select></div>
        <div className="col-md-4"><label className="form-label">Groupe (si visibilité « groupe »)</label>
          <select name="groupe_id" className="form-select" defaultValue={initial?.groupe_id || ''}><option value="">—</option>{(groupes || []).map((g) => <option key={g.id} value={g.id}>{g.nom}</option>)}</select></div>
        <div className="col-md-4"><label className="form-label">Invités max. / inscription</label><input name="invites_max" type="number" min="0" className="form-control" defaultValue={initial?.invites_max ?? ''} /></div>
        <div className="col-12"><label className="form-label">Image mise en avant (JPEG/PNG, 3 Mo max)</label>
          <input type="file" accept="image/jpeg,image/png" className="form-control" onChange={lireImage} />
          {image && <img src={image} alt="Aperçu" className="mt-2 rounded" style={{ maxHeight: 120 }} />}</div>
        <div className="col-12"><label className="form-label">Description</label><textarea name="description" rows="4" className="form-control" defaultValue={initial?.description ? initial.description.replace(/<[^>]+>/g, '') : ''}></textarea></div>

        {/* Options en boutons : les cases à cocher étaient des cibles trop
            petites au doigt, et incohérentes avec les autres formulaires. */}
        <div className="col-12">
          <label className="form-label d-block">Options de l'activité</label>
          <div className="row g-2 adlt-options-activite">
            <div className="col-6 col-lg-3">
              <BoutonOption actif={listeAttente} onToggle={setListeAttente}>Liste d'attente</BoutonOption>
            </div>
            <div className="col-6 col-lg-3">
              <BoutonOption actif={invitesOk} onToggle={setInvitesOk}>Invités autorisés</BoutonOption>
            </div>
            <div className="col-6 col-lg-3">
              <BoutonOption actif={excuse} onToggle={setExcuse} aide="Activité obligatoire">Option « excuse »</BoutonOption>
            </div>
            <div className="col-6 col-lg-3">
              <BoutonOption actif={clubhouse} onToggle={setClubhouse}
                aide={clubhouse ? 'Réservé automatiquement et validé.' : null}>Club-house nécessaire</BoutonOption>
            </div>
          </div>
        </div>
      </div>
      <button className="btn btn-primary mt-3" disabled={busy}>{edition ? 'Enregistrer les modifications' : "Publier l'activité"}</button>
    </form>
  );
}
